import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { getMediaUrl } from '@onesource/services';
import DOMPurify from 'dompurify';
import { CiImageOn } from 'react-icons/ci';

import WhosOnline from '#/components/whos-online';

interface SpaceNoAccessPageProps {
  name: string;
  bannerImage?: string | null;
}

/**
 * Space No Access Page
 * Displayed when user navigates to a space they don't have access to.
 * Shows the space name and banner with a lock overlay.
 */
export const SpaceNoAccessPage = ({ name, bannerImage }: SpaceNoAccessPageProps) => {
  const theme = useTheme();
  const bannerUrl = bannerImage ? getMediaUrl(bannerImage) : null;

  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
      <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 'calc(100% - 250px)' } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          sx={{
            mb: 2,
            fontFamily: '"Figtree", sans-serif',
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 400,
            color: theme.palette.grey[600],
          }}
        >
          <Box component="span" style={{ color: theme.palette.grey[600] }}>
            Community
          </Box>
          <Typography component="span" sx={{ fontSize: 16, fontWeight: 700 }}>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(name) }} />
          </Typography>
        </Breadcrumbs>

        <Container maxWidth="md" sx={{ px: '0 !important' }}>
          {/* Banner Image */}
          <Box sx={{ position: 'relative' }}>
            {bannerUrl ? (
              <Box
                sx={{
                  maxWidth: { xs: '100%', md: 918 },
                  height: { xs: 160, md: 310 },
                  borderRadius: '12px',
                  backgroundImage: `url(${bannerUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 160, md: 279 },
                  borderRadius: '15px',
                  overflow: 'hidden',
                  bgcolor: '#00000066',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CiImageOn size={64} color="#d8aaa2" />
              </Box>
            )}

            {/* Lock overlay */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: '#00000066',
                borderRadius: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 0, md: '6px' },
                  fontFamily: '"Figtree", sans-serif',
                  fontWeight: 600,
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  color: theme.palette.common.white,
                }}
              >
                <Typography component="span" sx={{ fontSize: 30, fontWeight: 700 }}>
                  It looks like you’re not a member yet...{' '}
                </Typography>{' '}
                <Typography
                  component="span"
                  sx={{ fontFamily: '"Fraunces", sans-serif', fontSize: 12, fontWeight: 400 }}
                >
                  Learn more about the membership{' '}
                </Typography>{' '}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <WhosOnline />
    </Box>
  );
};
